import {
	doc,
	getFirestore,
	setDoc,
	collection,
	getDocs,
	query,
	onSnapshot,
	where,
	WhereFilterOp,
	runTransaction,
	getDoc,
	orderBy,
	deleteDoc,
	updateDoc,
	documentId,
	writeBatch,
} from "firebase/firestore";
import { app } from "./firebaseApp";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
	getAuth,
	createUserWithEmailAndPassword,
	onAuthStateChanged,
	signOut,
	signInWithEmailAndPassword,
	signInWithPhoneNumber,
	ConfirmationResult,
	RecaptchaVerifier,
	PhoneAuthProvider,
	linkWithCredential,
	PhoneAuthCredential,
	deleteUser,
} from "firebase/auth";
import { storageApi } from "./storage";

import { getFunctions, httpsCallable } from "firebase/functions";
import { TDoctor, TNewDoctor, TUser, TProfile } from "@shared/entities";
import { FieldPath, FieldValues } from "react-hook-form";
import { TRequest } from "@shared/entities";

const db = getFirestore(app);
const auth = getAuth(app);

const functions = getFunctions(app);

// Keep the registration session separate until its profile has been accepted.
// Otherwise the main auth observer redirects away from the signup/error form.
function registrationApp() {
	return getApps().some((item) => item.name === "client-registration")
		? getApp("client-registration") : initializeApp(app.options, "client-registration");
}

type Entity = { id?: string };

export type TCollection =
	| "profiles"
	| "requests"
	| "payments"
	| "doctors"
	| "brokers"
	| "pharmacy_products"
	| "pharmacy_orders"
	| "leads"
	| "tasks"
	| "settings"
	| "document_templates"
	| "admin_client_data"
	| "automations";

export type LogPayload = {
	message: string;
	severity: "DEBUG" | "INFO" | "NOTICE" | "WARNING" | "ERROR" | "CRITICAL" | "ALERT" | "EMERGENCY";
	userId?: string;
	[key: string]: any; // Allows any additional properties
};

export const firebaseApi = {
	storage: storageApi,
	firestore: {
		async createDocumentIfNotExists<T extends Entity>(payload: {
			item: T;
			collectionName: TCollection;
		}) {
			if (!payload.item.id) {
				return;
			}
			const docRef = doc(db, payload.collectionName, payload.item.id);

			try {
				const result = await runTransaction(db, async (transaction) => {
					const docSnapshot = await transaction.get(docRef);

					if (docSnapshot.exists()) {
						return false; // Instead of throwing, return a failure status
					}

					transaction.set(docRef, payload.item);
					return true; // Successfully created
				});

				if (!result) {
					console.log("Document already exists, not creating a new one.");
				} else {
					console.log("Document created successfully.");
				}
			} catch (error) {
				console.error("Transaction failed:", error);
			}
		},
		subscribeDoc: <T>({
			collectionName,
			id,
			callback,
		}: {
			callback: (data: T | null) => void;
			id: string;
			collectionName: TCollection;
		}) => {
			const docRef = doc(db, collectionName, id);
			return onSnapshot(docRef, (snapshot) => {
				if (snapshot.exists()) {
					return callback({ ...snapshot.data(), id: snapshot.id } as T);
				}
				return callback(null);
			});
		},
		list: async <T extends FieldValues>({
			collectionName,
			filter = [],
			sort = [],
		}: {
			collectionName: TCollection;
			filter?: { name: FieldPath<T>; value: any; operator: WhereFilterOp }[];
			sort?: { name: FieldPath<T>; by: "asc" | "desc" }[];
		}) => {
			try {
				const _where = filter.map((f) => where(f.name, f.operator, f.value));
				const sorts = sort.map((s) => orderBy(s.name, s.by));
				const q = query(collection(db, collectionName), ..._where, ...sorts);
				const querySnapshot = await getDocs(q);
				if (!querySnapshot.empty) {
					return querySnapshot.docs.map((item) => ({
						...item.data(),
						id: item.id,
					})) as unknown as T[];
				} else {
					return [];
				}
			} catch (error: any) {
				// todo: add error handling
				return [];
			}
		},
		/** Fetch documents by their document id, in chunks of 30 (Firestore's
		 *  `in` cap), so the caller can pass any number of ids. Keyed on the real
		 *  document id — NOT a stored `id` field, which older documents may lack.
		 *  Errors throw instead of returning [], so a failure is visible. */
		listByIds: async <T extends Entity>({
			collectionName,
			ids,
		}: {
			collectionName: TCollection;
			ids: string[];
		}): Promise<T[]> => {
			const unique = [...new Set(ids.filter(Boolean))];
			const pages: Promise<T[]>[] = [];
			for (let i = 0; i < unique.length; i += 30) {
				const part = unique.slice(i, i + 30);
				pages.push(
					getDocs(query(collection(db, collectionName), where(documentId(), "in", part))).then(
						(snap) => snap.docs.map((d) => ({ ...d.data(), id: d.id }) as unknown as T),
					),
				);
			}
			return (await Promise.all(pages)).flat();
		},
		/** Update several documents (possibly in different collections) in one
		 *  atomic batch: either every write lands or none does. Keys may be dotted
		 *  field paths ("documents.ID") so sibling fields are left untouched.
		 *  Undefined values are dropped; FieldValue sentinels pass through. */
		updateAtomically: async (
			writes: Array<{ collectionName: TCollection; id: string; updates: AnyObject }>,
		): Promise<void> => {
			const batch = writeBatch(db);
			for (const { collectionName, id, updates } of writes) {
				batch.update(doc(db, collectionName, id), removeUndefinedFields(updates));
			}
			await batch.commit();
		},
		/** Apply the same partial update to many documents. Batched (≤450 writes
		 *  per commit, under Firestore's 500 cap) so there is no limit on how many
		 *  can be updated at once, and each batch is atomic. */
		updateMany: async <T extends Entity>({
			collectionName,
			ids,
			updates,
		}: {
			collectionName: TCollection;
			ids: string[];
			updates: Partial<T>;
		}): Promise<number> => {
			const unique = [...new Set(ids.filter(Boolean))];
			const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
			for (let i = 0; i < unique.length; i += 450) {
				const batch = writeBatch(db);
				for (const id of unique.slice(i, i + 450)) batch.update(doc(db, collectionName, id), clean);
				await batch.commit();
			}
			return unique.length;
		},
		get: async <T extends Entity>({
			collectionName,
			id,
		}: {
			id: string;
			collectionName: TCollection;
		}) => {
			const docRef = doc(db, collectionName, id);

			const snap = await getDoc(docRef);
			if (snap.exists()) {
				return { ...snap.data(), id: docRef.id } as T;
			}

			return null;
		},
		set: async <T extends Entity>({
			collectionName,
			item,
		}: {
			item: T;
			collectionName: TCollection;
		}) => {
			const docRef = item.id
				? doc(db, collectionName, item.id)
				: doc(collection(db, collectionName));

			const safeDoc = removeUndefinedFields(item);

			await setDoc(docRef, { ...safeDoc, id: docRef.id }, { merge: true });

			return { ...item, id: docRef.id };
		},
		update: async <T extends Entity>({
			collectionName,
			item,
		}: {
			item: T;
			collectionName: TCollection;
		}) => {
			if (!item.id) return;

			const docRef = doc(db, collectionName, item.id);
			await updateDoc(docRef, item);
		},
		create: async <T extends Entity>({
			collectionName,
			item,
		}: {
			item: T;
			collectionName: TCollection;
		}) => {
			const docRef = item.id
				? doc(db, collectionName, item.id)
				: doc(collection(db, collectionName));
			const payload = { ...item, id: docRef.id };
			await setDoc(docRef, payload);
			return payload as T;
		},
		delete: async ({ collectionName, id }: { id: string; collectionName: TCollection }) => {
			const docRef = doc(db, collectionName, id);
			await deleteDoc(docRef);
			return { success: true };
		},
	},

	auth: {
		register: async ({
			email,
			password,
		}: {
			email: string;
			password: string;
		}): Promise<TUser> => {
			const signupAuth = getAuth(registrationApp());
			await signupAuth.authStateReady();
			const res = signupAuth.currentUser?.email?.toLowerCase() === email.toLowerCase()
				? await signInWithEmailAndPassword(signupAuth, email, password)
				: await createUserWithEmailAndPassword(signupAuth, email, password);

			if (res.user) {
				const token = await res.user.getIdTokenResult();
				if (token) {
					const user: TUser = Object.assign<TUser, Partial<TUser>>(res.user, {
						admin: !!token.claims.admin,
						issuingDoctor: !!token.claims.issuingDoctor,
						recommendingDoctor: !!token.claims.recommendingDoctor,
						broker: !!token.claims.broker,
					});
					return user;
				}
			}
			return res.user;
		},
		completeRegistration: async (profile: Omit<TProfile, "id">) => {
			const call = httpsCallable<Omit<TProfile, "id">, { profile: TProfile }>(getFunctions(registrationApp()), "completeClientRegistration");
			return (await call(JSON.parse(JSON.stringify(profile)))).data.profile;
		},
		discardRegistration: async (user: TUser) => {
			const saved = await getDoc(doc(getFirestore(registrationApp()), "profiles", user.uid));
			if (!saved.exists()) await deleteUser(user);
		},
		endRegistration: async () => { await signOut(getAuth(registrationApp())); },
		logout: () => {
			return signOut(auth);
		},
		login: async (email: string, password: string): Promise<{ user: TUser }> => {
			const res = await signInWithEmailAndPassword(auth, email, password);
			if (res.user) {
				const token = await res.user.getIdTokenResult();
				if (token) {
					const user: TUser = Object.assign<TUser, Partial<TUser>>(res.user, {
						admin: !!token.claims.admin,
						issuingDoctor: !!token.claims.issuingDoctor,
						recommendingDoctor: !!token.claims.recommendingDoctor,
						broker: !!token.claims.broker,
					});
					return { user };
				}
			}
			return res;
		},

		onUser: (callback: (user: TUser | null) => void) => {
			return onAuthStateChanged(auth, async (user) => {
				if (user) {
					const token = await user.getIdTokenResult();
					if (token) {
						const _user: TUser = Object.assign<TUser, Partial<TUser>>(user, {
							admin: !!token.claims.admin,
							issuingDoctor: !!token.claims.issuingDoctor,
							recommendingDoctor: !!token.claims.recommendingDoctor,
							broker: !!token.claims.broker,
						});
						callback(_user);
					}
				} else {
					callback(user);
				}
			});
		},
		signInWithPhone: async (
			phoneNumber: string,
			recaptchaVerifier: RecaptchaVerifier,
		): Promise<ConfirmationResult> => {
			try {
				// Format phone number to E.164 format
				const formattedPhone = formatPhoneToE164(phoneNumber);
				console.log("formattedPhone", formattedPhone);
				return await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
			} catch (error) {
				console.log("error", error);
				throw error;
			}
		},
		confirmPhoneCode: async (
			confirmationResult: ConfirmationResult,
			code: string,
		): Promise<{ user: TUser }> => {
			try {
				console.log("code", code);
				const res = await confirmationResult.confirm(code);
				console.log("res", res);
				if (res.user) {
					const token = await res.user.getIdTokenResult();
					if (token) {
						const user: TUser = Object.assign<TUser, Partial<TUser>>(res.user, {
							admin: !!token.claims.admin,
							issuingDoctor: !!token.claims.issuingDoctor,
							recommendingDoctor: !!token.claims.recommendingDoctor,
						});
						return { user };
					}
				}
				return { user: res.user };
			} catch (error) {
				console.log("error", error);
				throw error;
			}
		},
		verifyPhoneNumber: async (
			phoneNumber: string,
			recaptchaVerifier: RecaptchaVerifier,
		): Promise<string> => {
			try {
				// Format phone number to E.164 format
				const formattedPhone = formatPhoneToE164(phoneNumber);
				console.log("verifyPhoneNumber - formattedPhone", formattedPhone);
				const provider = new PhoneAuthProvider(auth);
				const verificationId = await provider.verifyPhoneNumber(
					formattedPhone,
					recaptchaVerifier,
				);
				console.log("verificationId", verificationId);
				return verificationId;
			} catch (error) {
				console.log("verifyPhoneNumber error", error);
				throw error;
			}
		},
		linkPhoneCredential: async (
			user: TUser,
			verificationId: string,
			code: string,
		): Promise<TUser> => {
			try {
				const credential = PhoneAuthProvider.credential(
					verificationId,
					code,
				) as PhoneAuthCredential;
				const result = await linkWithCredential(user, credential);

				if (result.user) {
					const token = await result.user.getIdTokenResult();
					if (token) {
						const linkedUser: TUser = Object.assign<TUser, Partial<TUser>>(result.user, {
							admin: !!token.claims.admin,
							issuingDoctor: !!token.claims.issuingDoctor,
							recommendingDoctor: !!token.claims.recommendingDoctor,
							broker: !!token.claims.broker,
						});
						return linkedUser;
					}
				}
				return result.user;
			} catch (error) {
				console.log("linkPhoneCredential error", error);
				throw error;
			}
		},
	},

	api: {
		async getCoinsConfig(): Promise<{ coinsPerNis: number; pharmacyEarnPercent: number; requestEarnPercent: number }> {
			const docRef = doc(db, "settings", "coins");
			const snap = await getDoc(docRef);
			const data = snap.data() ?? {};
			return {
				coinsPerNis: (data.coinsPerNis as number) ?? 10,
				pharmacyEarnPercent: (data.pharmacyEarnPercent as number) ?? 10,
				requestEarnPercent: (data.requestEarnPercent as number) ?? 10,
			};
		},

		async updateCoinsConfig(config: { pharmacyEarnPercent: number; requestEarnPercent: number }): Promise<{ success: boolean; error?: any }> {
			try {
				const func = httpsCallable(functions, "updateCoinsConfig");
				const response = await func(config);
				return { success: true, ...(response.data as any) };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async adminAdjustCoins(payload: { userId: string; delta: number; reason: string }): Promise<{ success: boolean; newBalance?: number; error?: any }> {
			try {
				const func = httpsCallable(functions, "adminAdjustCoins");
				const response = await func(payload);
				return response.data as { success: boolean; newBalance: number };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async parsePrescriptionsDocument(payload: { clientId: string }): Promise<{ success: boolean; prescriptions?: { opensAt: number; label?: string }[]; error?: any }> {
			try {
				// Analysis sends the file to Claude — can take a while, so extend the
				// callable timeout beyond the 70s default.
				const func = httpsCallable(functions, "parsePrescriptionsDocument", { timeout: 300_000 });
				const response = await func(payload);
				return response.data as { success: boolean; prescriptions: { opensAt: number; label?: string }[] };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async confirmPrescriptions(payload: { clientId: string; prescriptions: { opensAt: number; label?: string }[] }): Promise<{ success: boolean; count?: number; error?: any }> {
			try {
				const func = httpsCallable(functions, "confirmPrescriptions");
				const response = await func(payload);
				return response.data as { success: boolean; count: number };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async parseIdDocument(payload: { clientId: string }): Promise<{ success: boolean; extracted?: { idNumber?: string; firstName?: string; lastName?: string; dob?: number }; idNumberMismatch?: boolean; dobConflict?: boolean; currentDob?: number | null; error?: any }> {
			try {
				// Analysis sends the file to Claude — can take a while, so extend the
				// callable timeout beyond the 70s default.
				const func = httpsCallable(functions, "parseIdDocument", { timeout: 300_000 });
				const response = await func(payload);
				return response.data as { success: boolean; extracted: { idNumber?: string; firstName?: string; lastName?: string; dob?: number }; idNumberMismatch: boolean; dobConflict: boolean; currentDob: number | null };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async confirmIdDetails(payload: { clientId: string; dob: number }): Promise<{ success: boolean; birthdayTaskAt?: number; error?: any }> {
			try {
				const func = httpsCallable(functions, "confirmIdDetails");
				const response = await func(payload);
				return response.data as { success: boolean; birthdayTaskAt: number };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async parseLicenseDocument(payload: { clientId: string }): Promise<{
			success: boolean;
			expiresAt?: number;
			startsAt?: number;
			licenseNumber?: string;
			expired?: boolean;
			opensAt?: number;
			error?: any;
		}> {
			try {
				const func = httpsCallable(functions, "parseLicenseDocument", { timeout: 300_000 });
				const response = await func(payload);
				return response.data as {
					success: boolean;
					expiresAt: number;
					startsAt?: number;
					licenseNumber?: string;
					expired: boolean;
					opensAt?: number;
				};
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async confirmLicenseRenewal(payload: {
			clientId: string;
			expiresAt: number;
			licenseNumber?: string;
		}): Promise<{ success: boolean; startAt?: number; dueAt?: number; error?: any }> {
			try {
				const func = httpsCallable(functions, "confirmLicenseRenewal");
				const response = await func(payload);
				return response.data as { success: boolean; startAt: number; dueAt: number };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async deletePharmacyOrder(payload: {
			orderId: string;
		}): Promise<{ success: boolean; coinsRefunded?: number; coinsClawedBack?: number; error?: any }> {
			try {
				const func = httpsCallable(functions, "deletePharmacyOrder");
				const response = await func(payload);
				return response.data as {
					success: boolean;
					coinsRefunded: number;
					coinsClawedBack: number;
				};
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async createPharmacyOrderWithCoins(orderData: {
			items: Array<{ product: any; quantity: number }>;
			deliveryDetails: { fullName: string; phone: string; address: string; city: string; postalCode: string; deliveryNotes: string };
			total: number;
			coinsToUse: number;
			userId?: string;
			userEmail?: string;
		}): Promise<{ success: boolean; orderId?: string; error?: any }> {
			try {
				const func = httpsCallable(functions, "createPharmacyOrderWithCoins");
				const response = await func(orderData);
				return response.data as { success: boolean; orderId: string };
			} catch (error: any) {
				return { success: false, error: error.message };
			}
		},

		async uiLogs(payload: LogPayload) {
			try {
				const func = httpsCallable(functions, "uiLogs");

				const response = await func(payload);
				return { success: true, data: response.data };
			} catch (error: any) {
				const code = error.code;
				const message = error.message;
				const details = error.details;
				console.error(code, message, details);
				return { success: false, data: null };
			}
		},

		async createDoctor(
			doctor: TNewDoctor,
		): Promise<{ success: boolean; error?: any; data?: TDoctor }> {
			const httpFunction = httpsCallable(functions, "createDoctor");

			const data: any = (await httpFunction(doctor)).data;
			return data;
		},

		async deleteDoctor(id: string): Promise<{ success: boolean; error?: any }> {
			const httpFunction = httpsCallable(functions, "deleteDoctor");

			const data: any = (await httpFunction({ id })).data;
			return data;
		},

		async resetDoctorPassword(
			id: string,
		): Promise<{ success: boolean; error?: any; data?: { email: string } }> {
			const httpFunction = httpsCallable(functions, "resetDoctorPassword");

			const data: any = (await httpFunction({ id })).data;
			return data;
		},

		async createBroker(broker: any): Promise<{ success: boolean; error?: any; data?: any }> {
			const httpFunction = httpsCallable(functions, "createBroker");

			const data: any = (await httpFunction(broker)).data;
			return data;
		},

		async updateClientProfile(clientId: string, updates: Partial<TProfile>): Promise<TProfile> {
			const call = httpsCallable<{ clientId: string; updates: Partial<TProfile> }, { profile: TProfile }>(functions, "updateClientProfile");
			return (await call(JSON.parse(JSON.stringify({ clientId, updates })))).data.profile;
		},

		async createClient(
			client: import("@shared/entities").TNewClient,
		): Promise<{
			success: boolean;
			error?: any;
			data?: import("@shared/entities").TProfile;
			/** Set when `error` is "duplicate_id_number": the client that owns it. */
			existingClientId?: string;
		}> {
			const httpFunction = httpsCallable(functions, "createClient");

			const data: any = (await httpFunction(client)).data;
			return data;
		},

		async deleteClient(id: string): Promise<{ success: boolean; error?: any }> {
			const httpFunction = httpsCallable(functions, "deleteClient");
			const data: any = (await httpFunction({ id })).data;
			return data;
		},

		async sendClientJoinLink(
			clientId: string,
		): Promise<{ success: boolean; error?: any; data?: { email: string } }> {
			const httpFunction = httpsCallable(functions, "sendClientJoinLink");
			const data: any = (await httpFunction({ clientId })).data;
			return data;
		},

		async deleteBroker(id: string): Promise<{ success: boolean; error?: any }> {
			const httpFunction = httpsCallable(functions, "deleteBroker");

			const data: any = (await httpFunction({ id })).data;
			return data;
		},

		async resetBrokerPassword(
			id: string,
		): Promise<{ success: boolean; error?: any; data?: { email: string } }> {
			const httpFunction = httpsCallable(functions, "resetBrokerPassword");

			const data: any = (await httpFunction({ id })).data;
			return data;
		},

		async recordSubmissionDeclaration(data: { requestId: string; version: string; renewalContactConsent: boolean }) {
			const call = httpsCallable(functions, "recordSubmissionDeclaration");
			return (await call(data)).data as { version: string; acceptedAt: number; renewalContactConsent: boolean };
		},

		async generatePaymentLink(data: { request: TRequest; amount: number; title: string }) {
			const generatePaymentLink = httpsCallable(functions, "generatePaymentLink");
			return (await generatePaymentLink(data)).data as {
				success: boolean;
				paymentLink: string;
				queryParams?: any;
				errMessage?: string;
			};
		},

		renderDocumentToPdf: httpsCallable<
			{ requestId: string; templateId: string; content: object; title: string; signaturePosition: string },
			{ success: boolean; pdfUrl?: string; pdfPath?: string; size?: number; error?: string }
		>(functions, "renderDocumentToPdf"),
	},
};

/**
 * Formats a phone number to E.164 format
 * Assumes Israeli numbers (country code +972) if no country code is provided
 * @param phone - Phone number in various formats
 * @returns Phone number in E.164 format (e.g., +972501234567)
 */
function formatPhoneToE164(phone: string): string {
	// Remove all non-digit characters except +
	let cleaned = phone.replace(/[^\d+]/g, "");

	// If it already starts with +, return as is (assuming it's already in E.164)
	if (cleaned.startsWith("+")) {
		return cleaned;
	}

	// Remove leading zeros
	cleaned = cleaned.replace(/^0+/, "");

	// If it starts with 972 (Israel country code without +), add +
	if (cleaned.startsWith("972")) {
		return `+${cleaned}`;
	}

	// Otherwise, assume it's an Israeli number and add +972
	// Israeli numbers are typically 9 digits after removing leading 0
	return `+972${cleaned}`;
}

type AnyObject = Record<string, any>;

/** Plain data this function is safe to walk into. A class instance — a
 *  FieldValue sentinel like arrayUnion(), a Date — must be passed through
 *  untouched: Object.entries() on one yields {} and silently destroys it. */
function isPlainObject(value: unknown): boolean {
	if (typeof value !== "object" || value === null) return false;
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}

function removeUndefinedFields<T extends AnyObject | AnyObject[]>(obj: T): T {
	if (Array.isArray(obj)) {
		return obj
			.filter((value) => value !== undefined) // Filter undefined from arrays
			.map((item) =>
				isPlainObject(item) || Array.isArray(item)
					? removeUndefinedFields(item) // Recurse for array items
					: item,
			) as T;
	} else if (isPlainObject(obj)) {
		return Object.fromEntries(
			Object.entries(obj)
				.filter(([, value]) => value !== undefined)
				.map(([key, value]) => [
					key,
					isPlainObject(value) || Array.isArray(value) ? removeUndefinedFields(value) : value,
				]),
		) as T;
	}
	return obj;
}
